import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { api, resolveUrl } from '../lib/api';
import { t } from '../lib/i18n';

const INTERVAL = 6000;

const Slide = ({ banner, active, single }) => {
  const src = resolveUrl(banner.image);
  const Tag = banner.link ? 'a' : 'div';
  const linkProps = banner.link ? { href: banner.link, target: '_blank', rel: 'noopener noreferrer sponsored', onClick: () => api.post(`/promo-banners/${banner.id}/click`).catch(() => {}), 'data-testid': 'promo-banner-link' } : {};
  return (
    <Tag {...linkProps} aria-hidden={!active} tabIndex={active ? 0 : -1} data-testid={`promo-slide-${banner.id}`}
      className={`promo-strip live-theme group block rounded-2xl overflow-hidden border border-white/10 bg-[#0b0b0f] shadow-[0_24px_60px_-28px_rgba(0,0,0,.9)] transition-[opacity,transform] duration-700 ease-out ${single ? 'relative hover:-translate-y-0.5' : `absolute inset-0 ${active ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-[1.02] pointer-events-none z-0'}`}`}>
      <img src={src} alt={banner.alt || 'Promotion'} className={`block w-full object-cover transition-transform duration-700 group-hover:scale-[1.02] ${single ? 'h-auto max-h-[420px]' : 'h-full'}`} data-testid="promo-banner" />
      <span className="absolute top-3 start-3 px-2 py-0.5 rounded-full bg-black/40 backdrop-blur text-[9px] font-black uppercase tracking-[0.18em] text-white/70 border border-white/10" data-testid="promo-banner-label">{banner.sponsor ? `${t('Sponsorisé')} · ${banner.sponsor}` : t('Sponsorisé')}</span>
      {banner.link && <span className="absolute bottom-3 end-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white text-black text-xs font-bold shadow-lg opacity-0 translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0" data-testid="promo-banner-cta">{t('Découvrir')} <ArrowRight size={13} className="rtl:-scale-x-100" /></span>}
    </Tag>
  );
};

export const PromoCarousel = ({ banners, placement }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [ratio, setRatio] = useState(4);
  const seen = useRef(new Set());
  const touchX = useRef(null);
  const count = banners.length;
  const current = banners[Math.min(index, count - 1)];

  useEffect(() => {
    if (!current || seen.current.has(current.id)) return;
    seen.current.add(current.id);
    api.post(`/promo-banners/${current.id}/impression`).catch(() => {});
  }, [current]);

  useEffect(() => {
    if (count < 2 || paused) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % count), INTERVAL);
    return () => clearInterval(id);
  }, [count, paused]);

  useEffect(() => {
    if (count < 2) return;
    const img = new Image();
    img.onload = () => img.naturalHeight && setRatio(Math.min(6, Math.max(2.4, img.naturalWidth / img.naturalHeight)));
    img.src = resolveUrl(banners[0].image);
  }, [banners, count]);

  if (!count) return null;
  if (count === 1) return <div className="my-6 md:my-8" data-testid={`promo-banner-${placement}`}><Slide banner={banners[0]} active single /></div>;

  const go = (i) => setIndex((i + count) % count);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
  };

  return (
    <div className="my-6 md:my-8" data-testid={`promo-banner-${placement}`} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      <div className="relative w-full max-h-[420px]" style={{ aspectRatio: `${ratio} / 1` }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="promo-carousel" role="region" aria-roledescription="carousel" aria-label="Bannières sponsorisées">
        {banners.map((b, i) => <Slide key={b.id} banner={b} active={i === index} />)}
        <div className="absolute bottom-3 inset-x-0 z-20 flex items-center justify-center gap-1.5 pointer-events-none" data-testid="promo-carousel-dots">
          {banners.map((b, i) => (
            <button key={b.id} type="button" onClick={() => go(i)} aria-label={`Bannière ${i + 1}`} aria-current={i === index} data-testid={`promo-dot-${i}`}
              className={`pointer-events-auto h-1.5 rounded-full transition-[width,background-color] duration-500 ${i === index ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'}`}>
              {i === index && !paused && <span key={`${index}-${paused}`} className="block h-full rounded-full bg-[#E50914] promo-dot-progress" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PromoCarousel;
